import { WarehouseGrid, Zone } from "./schema/warehouse-grid";
import { pointInRectangle } from "./zone-rectangles";

/**
 * Resolves which zone a bin belongs to within a warehouse layout.
 *
 * The bin name is looked up against `grid.binCoordinates` to get a cell
 * coordinate, then hit-tested against each non-default zone's rectangles.
 * The first matching zone wins (zones don't overlap by builder invariant,
 * so any match is unambiguous).
 *
 * Returns:
 *   - the zone id of the matching rectangle, or
 *   - the default zone's id when no rectangle matches (including when the
 *     bin name isn't in `binCoordinates`), or
 *   - null when the layout has no zones at all.
 *
 * Default zone resolution is two-tiered for defensive back-compat:
 *   1. The zone marked `isDefault: true` is the canonical default per the
 *      schema's stated invariant ("exactly one per layout once zones are
 *      introduced"). Preferred when present.
 *   2. If no zone has `isDefault: true` (schema invariant violated by an
 *      older layout or hand-edited doc), the FIRST zone in `zoneSequence`
 *      is treated as the implicit default. Per the model: every layout
 *      with zones must have *a* default; we synthesize one rather than
 *      crash callers.
 *   3. If even `zoneSequence` is empty / missing, return the first entry
 *      in `zones` as the last-resort fallback.
 */
export function resolveBinZone(
  binName: string,
  grid: WarehouseGrid,
): string | null {
  const zones = grid.zones;
  if (!zones) {
    return null;
  }
  const zoneEntries = Object.entries(zones);
  if (zoneEntries.length === 0) {
    return null;
  }

  const defaultZoneId = resolveDefaultZoneIdFromEntries(grid, zoneEntries);

  // `binCoordinates` is stored upper-cased by the layout builder
  // (`generate-bin-coordinates.ts` does `[fullBinName.toUpperCase()]`),
  // but callers pass `item.bin` straight through whatever case the FG
  // metafield held. Try the verbatim key first (cheap; covers
  // callers that pre-uppercased), then the upper-cased form. Without
  // this fallback, any mixed/lower-cased bin name silently routes to
  // the default zone — same failure mode as a missing coordinate.
  const coord =
    grid.binCoordinates?.[binName] ??
    grid.binCoordinates?.[binName.toUpperCase()];
  if (!coord) {
    return defaultZoneId;
  }

  for (const [zoneId, zone] of zoneEntries) {
    if (zone.isDefault) {
      continue;
    }
    for (const rect of zone.rectangles) {
      if (pointInRectangle(coord, rect)) {
        return zoneId;
      }
    }
  }
  return defaultZoneId;
}

/**
 * Picks the layout's default zone id with defensive fallbacks. Returns
 * null only when the layout has no zones at all.
 *
 * Resolution order:
 *   1. The zone marked `isDefault: true` (schema-canonical — exactly one
 *      per layout once zones are introduced).
 *   2. The first entry in `zoneSequence` that exists in `zones` — the
 *      implicit default when the canonical `isDefault` marker is missing.
 *   3. Any entry in `zones` — last-resort fallback when `zoneSequence` is
 *      missing or empty.
 *
 * Used by `resolveBinZone` for bins that don't hit-test into any explicit
 * rectangle, and by external callers (e.g. pick-wave gates) that need to
 * route an FG without resolvable bins to the layout's catch-all zone.
 */
export function resolveDefaultZoneId(
  grid: Pick<WarehouseGrid, "zones" | "zoneSequence">,
): string | null {
  const zones = grid.zones;
  if (!zones) {
    return null;
  }
  const zoneEntries = Object.entries(zones);
  if (zoneEntries.length === 0) {
    return null;
  }
  return resolveDefaultZoneIdFromEntries(grid, zoneEntries);
}

function resolveDefaultZoneIdFromEntries(
  grid: Pick<WarehouseGrid, "zones" | "zoneSequence">,
  zoneEntries: [string, Zone][],
): string | null {
  // 1. Explicit isDefault marker (schema-recommended)
  const explicit = zoneEntries.find(([, z]) => z.isDefault)?.[0];
  if (explicit !== undefined) {
    return explicit;
  }
  // 2. First entry in zoneSequence that actually exists in zones
  if (grid.zoneSequence) {
    for (const zoneId of grid.zoneSequence) {
      if (Object.prototype.hasOwnProperty.call(grid.zones ?? {}, zoneId)) {
        return zoneId;
      }
    }
  }
  // 3. Any zone — last-resort fallback when zoneSequence is missing/empty
  return zoneEntries[0]?.[0] ?? null;
}

/**
 * Returns the earliest zone in `zoneSequence` that contains at least one of
 * the supplied bins. Used by pick-wave generation to gate which fresh FGs an
 * incoming picker at zone N can be assigned to.
 *
 * Skip semantics: a zone with none of the FG's bins is silently passed over.
 * If the FG's bins fall in zones [Z1, Z3, Z5] and the sequence is
 * [Z1, Z2, Z3, Z4, Z5], the first zone returned is Z1.
 *
 * Returns null when:
 *   - no bin names were supplied
 *   - the zone sequence is empty
 *   - no bin resolves to any zone (layout has no zones at all)
 *   - no resolved zone appears in the sequence (data drift between
 *     `zones` and `zoneSequence`)
 *
 * Caller decides what to do with null — typically reject the FG from
 * zone-routed picking and fall back to existing flow.
 */
export function resolveFirstZone(
  binNames: string[],
  grid: WarehouseGrid,
  zoneSequence: string[],
): string | null {
  if (binNames.length === 0 || zoneSequence.length === 0) {
    return null;
  }

  const zoneIdsForBins = new Set<string>();
  for (const binName of binNames) {
    const zoneId = resolveBinZone(binName, grid);
    if (zoneId !== null) {
      zoneIdsForBins.add(zoneId);
    }
  }
  if (zoneIdsForBins.size === 0) {
    return null;
  }

  for (const zoneId of zoneSequence) {
    if (zoneIdsForBins.has(zoneId)) {
      return zoneId;
    }
  }
  return null;
}
